import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import type { UnpackResponse } from "../../../api/src/lib";
import type { AdminController } from "../../../api/src/admin/admin.controller";
import { HttpStatusError } from "./useAdminRebalanceExplain";

// The all-firms roster from the staff-guarded `GET /admin/firms` endpoint
// (s-firm-scoped-admin-proposal-019ea1d9 R16) — `{ id, name }` per firm, used
// to populate the selector. Not scoped to the caller's firm (AC20).
export type AdminFirm = UnpackResponse<
  AdminController["getFirms"]
>["data"][number];

type WrappedFirms = UnpackResponse<AdminController["getFirms"]>;

// Terminal load states for the firm selector source (R28/AC21):
//   - `loading`: request in flight
//   - `ok`:      firms loaded; an empty array is the empty state (R28/AC21)
//   - `error`:   the firm-list source failed
export type AdminFirmsStatus = "loading" | "ok" | "error";

export interface UseAdminFirmsResult {
  status: AdminFirmsStatus;
  firms: AdminFirm[];
}

/**
 * Fetch the all-firms roster for the staff selector
 * (s-firm-scoped-admin-proposal-019ea1d9 R16/R28). GET-only. When the source
 * returns 0 firms or fails, the selector shows a distinct empty/error state and
 * the page issues 0 list requests (R28/AC21) — the caller gates the list query
 * on a chosen firm, which can only come from a non-empty selector.
 */
export function useAdminFirms(): UseAdminFirmsResult {
  const { getAccessTokenSilently } = useAuth0();

  const query = useQuery<AdminFirm[], HttpStatusError>({
    queryKey: ["admin", "firms"],
    retry: false,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const res = await fetch(`${process.env.REACT_APP_API_ROOT}/admin/firms`, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new HttpStatusError(res.status, `Request failed (${res.status})`);
      }
      const body = (await res.json()) as WrappedFirms;
      return body.data;
    },
  });

  if (query.isPending) {
    return { status: "loading", firms: [] };
  }
  if (query.isError) {
    return { status: "error", firms: [] };
  }
  return { status: "ok", firms: query.data };
}
